import { GridColDef } from "@mui/x-data-grid-pro";
import moment from "moment";

import type { PatientEncounter, Problem, Task } from "@/resources/types";
import {
  ActionsCell,
  AlertCell,
  AtsCell,
  CommentCell,
  LengthOfStayCell,
  LocationCell,
  PractitionersCell,
  PresentingProblemCell,
  StatusCell,
  SexAgeCell,
  ObservationDueCell,
  RiskScoreCell,
  getSexAgeValue,
  FallsCell,
  ArrivalTimeCell,
} from "@/components/PatientList/PatientListCells";
import { FullNameCell, getFullNameValue } from "@/components/TableCells";
import PatientActionsButton from "@/components/PatientActionButtons";
import { formatDuration } from "@/global";
import { getLengthOfStay } from "@/resources/encounters";

export const COLUMN_NAMES = [
  "patientName",
  "sex",
  "status",
  "riskScore.score",
  "nextObsDue",
  "falls",
  "allergies",
  "atsScore",
  "arrivalTime",
  "arrived",
  "location",
  "seniorDoctor",
  "doctor",
  "nurse",
  "problem",
  "comment",
  "tasks",
] as const;

// TODO do not understand the difference and why this fixes it
// solution came from https://github.com/mui/mui-x/issues/1376
// IMPORTANT perhaps I know the answer: I have encountered that
// such types of props, like renderCell, look like React components
// (i.e. functions that return JSX), but internally are used as
// plain functions. And then any of React features (ref forwarding,
// hooks, whatever) - break in runtime
const obsCellFunc = (props: any) => <ObservationDueCell {...props} />;
// This really should be the same as above, but it doesn't work
// const obsCellFuncLower = ObservationDueCell;

const patientProblemFormatter = (value: Problem) => {
  return value?.patient;
};

type Note = { created: string | number | Date };

export const notesToString = (notes: any) => {
  if (!notes || notes.length === 0) return "";

  const sortedNotes = notes.sort(
    (a: Note, b: Note) =>
      new Date(b.created).getTime() - new Date(a.created).getTime()
  );

  const notesText = sortedNotes
    .map((note: { text: any }) => note.text)
    .join("");

  return notesText;
};

export const dateToString = (value: moment.MomentInput) =>
  value ? moment(value).format("MMMM Do YYYY, h:mm:ss a") : "-";

export const formatLOS = (_: any, row: any) => {
  const patient = row as PatientEncounter;
  const LOS = getLengthOfStay(patient);
  return LOS ? formatDuration(moment.duration(LOS)) : "-";
};

export const tasksToString = (patientEncounter: PatientEncounter) => {
  if (!patientEncounter || !patientEncounter.tasks) return "";

  const activeTasks = patientEncounter.tasks.filter(
    (t) => t.status === "requested" || t.status === "done"
  );

  const requestedTasks = activeTasks
    .filter((t) => t.status === "requested")
    .map((task: Task) => task.type)
    .join(", ");

  const doneTasks = activeTasks
    .filter((t) => t.status === "done")
    .map((task: Task) => task.type)
    .join(", ");

  if (requestedTasks && doneTasks) {
    return `Requested: ${requestedTasks}\nDone: ${doneTasks}`;
  } else if (requestedTasks) {
    return `Requested: ${requestedTasks}`;
  } else {
    return "";
  }
};

export const patientListColumns = ([
  {
    field: "patientName",
    headerName: "Patient Name",
    minWidth: 100,
    width: 250,
    pinnable: true,
    valueGetter: getFullNameValue,
    renderCell: FullNameCell,
  },
  {
    field: "sex",
    headerName: "S (A)",
    minWidth: 90,
    width: 100,
    sortable: false,
    valueGetter: getSexAgeValue,
    renderCell: SexAgeCell,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 100,
    width: 100,
    renderCell: StatusCell,
  },
  {
    field: "riskScore.score",
    headerName: "Risk Score",
    minWidth: 50,
    width: 90,
    valueGetter: (_: any, row: any) => row.riskScore && row.riskScore.score,
    renderCell: RiskScoreCell,
  },
  {
    field: "nextObsDue",
    headerName: "Obs due",
    minWidth: 100,
    width: 100,
    valueFormatter: (value: any) => dateToString(value),
    renderCell: obsCellFunc,
  },
  {
    field: "falls",
    headerName: "Falls risk",
    sortable: false,
    minWidth: 75,
    width: 75,
    renderCell: FallsCell,
    valueGetter: (_: any, row: any) => row.frailty,
  },
  {
    field: "allergies",
    headerName: "Alerts",
    sortable: false,
    minWidth: 50,
    width: 100,
    renderCell: AlertCell,
  },
  {
    field: "atsScore",
    headerName: "ATS",
    minWidth: 50,
    width: 50,
    renderCell: AtsCell,
  },
  // Arrival Time is when `arrived` is in the future...
  {
    field: "arrivalTime",
    headerName: "Arrival Time",
    minWidth: 100,
    width: 100,
    valueGetter: (_: any, row: any) => row.arrived,
    valueFormatter: (value: any) => dateToString(value),
    renderCell: ArrivalTimeCell,
  },
  // ...LOS is when `arrived` is in the past
  {
    field: "arrived",
    headerName: "LOS",
    minWidth: 100,
    width: 100,
    valueFormatter: formatLOS,
    renderCell: LengthOfStayCell,
  },
  {
    field: "location",
    headerName: "Loc",
    minWidth: 100,
    width: 100,
    valueGetter: LocationCell,
  },
  {
    field: "seniorDoctor",
    headerName: "Snr Dr",
    minWidth: 100,
    width: 100,
    renderCell: PractitionersCell("snrDoctor"),
  },
  {
    field: "doctor",
    headerName: "Dr",
    minWidth: 100,
    width: 100,
    renderCell: PractitionersCell("doctor"),
  },
  {
    field: "nurse",
    headerName: "Nurse",
    minWidth: 100,
    width: 100,
    renderCell: PractitionersCell("nurse"),
  },
  {
    field: "problem",
    headerName: "Presenting problem",
    sortable: false,
    minWidth: 100,
    width: 250,
    valueFormatter: patientProblemFormatter,
    renderCell: (params) => <PresentingProblemCell row={params.row} />,
  },
  {
    field: "comment",
    headerName: "Comment",
    sortable: false,
    minWidth: 100,
    width: 250,
    valueGetter: (_: any, row: any) => row.comment?.text ?? "",
    renderCell: CommentCell,
  },
  {
    field: "tasks",
    headerName: "Tasks",
    sortable: false,
    minWidth: 170,
    width: 210,
    valueGetter: (_: any, row: any) => row as PatientEncounter,
    valueFormatter: (_: any, row: any) => tasksToString(row as PatientEncounter),
    renderCell: ActionsCell,
  },
  {
    field: "actions",
    type: "actions",
    resizable: false,
    sortable: false,
    width: 60,
    maxWidth: 60,
    renderCell: ({ row }) => <PatientActionsButton encounter={row} />,
  },
] as GridColDef[]);
